/**
 * EquipmentAlertsCard — alertas ativos + histórico do EQUIPMENT específico.
 *
 * Mostrado na aba "Alertas" do EquipmentDetail. Escopo restrito ao próprio
 * equipment — não precisa filtros de ativo (o próprio contexto já filtra).
 *
 * Estrutura (após #159):
 *   [ 🔴 Firing agora ]                — status='firing', bloco vermelho, botão Reconhecer
 *   [ ⚪ Aguardando resolução (ack) ]  — status='acknowledged', bloco cinza, botão Fechar
 *   [ Histórico ]                      — últimos N eventos, todos os tipos
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, CheckCheck, CheckCircle2, RefreshCcw, XCircle,
} from "lucide-react";
import toast from "react-hot-toast";

import { alertEventsApi } from "@/api/alerts";
import { alvoDoEvento } from "@/lib/eventoAlvo";
import { apiErrorMessage } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveAlerts, useAlertEvents } from "@/hooks/queries";
import type { ActiveAlert, AlertEvent, Severity } from "@/types";

import { Badge } from "./Badge";
import { Button } from "./Button";
import { Card, CardBody, CardHeader } from "./Card";
import { DataTable, type Column } from "./DataTable";
import { formatDate, timeAgo } from "@/lib/format";
import { SEVERIDADE_TOM } from "@/lib/severidade";

// Mapa compartilhado (lib/severidade.ts): este arquivo tinha a própria
// cópia, e eram dez ao todo. Ver o cabeçalho de lá.
const SEVERITY_TONE = SEVERIDADE_TOM;

const EVENT_TYPE_LABEL: Record<string, string> = {
  fired:                "Disparou",
  recovered:            "Recuperou",
  acknowledged:         "Reconhecido",
  renotified:           "Lembrete",
  auto_closed:          "Fechado auto",
  manually_closed:      "Fechado manual",
  notification_sent:    "Notificado",
  notification_failed:  "Falha de notificação",
};

const EVENT_TYPE_TONE: Record<string, "danger" | "success" | "warning" | "default"> = {
  fired:               "danger",
  recovered:           "success",
  acknowledged:        "default",
  renotified:          "warning",
  auto_closed:         "default",
  manually_closed:     "default",
  notification_sent:   "default",
  notification_failed: "warning",
};

interface Props {
  equipmentId: string;
  tenantId?: string;
}

export function EquipmentAlertsCard({ equipmentId, tenantId }: Props) {
  const qc = useQueryClient();
  // Mesmo papel das rotas de ack/close (`require_operator`) e do AlertBell.
  // Sem o gate, o viewer via os dois botões e levava 403 em toast.
  const canAck = useAuth().hasMinRole("operator");

  // Active alerts — filtra client-side por equipment (hook aceita só tenantId).
  const active = useActiveAlerts(tenantId);
  const forThisEquipment = (active.data ?? []).filter(
    (a) => a.equipment_id === equipmentId,
  );

  // #159: separa por status pra tratamento visual distinto
  const firing = forThisEquipment.filter((a) => a.status === "firing");
  const acked  = forThisEquipment.filter((a) => a.status === "acknowledged");

  // Histórico — hook aceita equipment_id direto no backend.
  // include_site_events: a queda de energia do site é gravada com
  // equipment_id NULL (é cross-device). Sem a flag, esta tela fica muda
  // exatamente no alerta mais grave que o ativo sofreu.
  const events = useAlertEvents({
    equipment_id:        equipmentId,
    include_site_events: true,
    tenant_id:           tenantId,
    limit:               100,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["active-alerts"] });
    void qc.invalidateQueries({ queryKey: ["alert-events"] });
  };

  const ackMut = useMutation({
    mutationFn: ({ ruleId, equipmentId: eid }: { ruleId: string; equipmentId: string }) =>
      alertEventsApi.acknowledge(ruleId, eid),
    onSuccess: () => {
      toast.success("Alerta reconhecido");
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const closeMut = useMutation({
    mutationFn: ({ ruleId, equipmentId: eid }: { ruleId: string; equipmentId: string }) =>
      alertEventsApi.close(ruleId, eid),
    onSuccess: () => {
      toast.success("Alerta fechado");
      invalidate();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<AlertEvent>[] = [
    {
      key: "created_at",
      header: "Quando",
      cell: (r) => (
        <div>
          <div className="text-sm">{timeAgo(r.created_at)}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(r.created_at)}
          </div>
        </div>
      ),
    },
    {
      key: "event_type",
      header: "Evento",
      cell: (r) => (
        <Badge tone={EVENT_TYPE_TONE[r.event_type] ?? "default"}>
          {EVENT_TYPE_LABEL[r.event_type] ?? r.event_type}
        </Badge>
      ),
    },
    {
      key: "severity",
      header: "Sev",
      cell: (r) =>
        r.severity ? (
          <Badge tone={SEVERITY_TONE[r.severity as Severity]}>{r.severity}</Badge>
        ) : (
          <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
        ),
      hideOnMobile: true,
    },
    {
      key: "message",
      header: "Mensagem",
      cell: (r) => {
        const alvo = alvoDoEvento(r);
        return (
          <span className="text-sm text-slate-700 dark:text-slate-300">
            {/* Marca o que NÃO é deste ativo. Sem isso, um evento do site
                inteiro se passaria por evento desta câmara fria — pior que
                não mostrar, porque parece específico e não é. */}
            {alvo.escopo === "site" && (
              <Badge tone="neutral" className="mr-1.5">
                {alvo.nome ? `Site ${alvo.nome}` : "Site"}
              </Badge>
            )}
            {r.message ?? "—"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* 🔴 Firing agora — bloco vermelho, chamada pra ação */}
      {firing.length > 0 && (
        <Card className="border-red-200 dark:border-red-900/60">
          <CardHeader
            title={
              <span className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertTriangle className="h-4 w-4" />
                Firing agora ({firing.length})
              </span>
            }
            subtitle="Alertas ativos aguardando reconhecimento."
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-surface-100 dark:divide-slate-800">
              {firing.map((a) => (
                <ActiveAlertRow
                  key={`${a.rule_id}-${a.equipment_id}`}
                  alert={a}
                  primaryAction={canAck && (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<CheckCheck className="h-4 w-4" />}
                      loading={ackMut.isPending}
                      onClick={() =>
                        ackMut.mutate({ ruleId: a.rule_id, equipmentId: a.equipment_id })
                      }
                    >
                      Reconhecer
                    </Button>
                  )}
                />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* ⚪ Reconhecidos — bloco cinza, aguardando recovery ou fechamento manual */}
      {acked.length > 0 && (
        <Card className="border-slate-200 dark:border-slate-700/60">
          <CardHeader
            title={
              <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                <CheckCircle2 className="h-4 w-4" />
                Reconhecidos ({acked.length})
              </span>
            }
            subtitle="Alguém está tratando. Se o problema não vai recuperar sozinho, use 'Fechar'."
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-surface-100 dark:divide-slate-800">
              {acked.map((a) => (
                <ActiveAlertRow
                  key={`${a.rule_id}-${a.equipment_id}`}
                  alert={a}
                  muted
                  primaryAction={canAck && (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<XCircle className="h-4 w-4" />}
                      loading={closeMut.isPending}
                      onClick={() =>
                        closeMut.mutate({ ruleId: a.rule_id, equipmentId: a.equipment_id })
                      }
                      title="Fechar sem esperar recovery (útil quando o problema foi tratado offline)"
                    >
                      Fechar
                    </Button>
                  )}
                />
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* Histórico */}
      <Card>
        <CardHeader
          title="Histórico de alertas"
          subtitle={`Últimos ${(events.data?.length ?? 0)} eventos desse ativo`}
          actions={
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCcw className="h-3.5 w-3.5" />}
              onClick={() => events.refetch()}
              loading={events.isFetching}
            >
              Atualizar
            </Button>
          }
        />
        <CardBody className="p-0">
          <DataTable
            data={events.data}
            columns={columns}
            loading={events.isLoading}
            rowKey={(r) => r.id}
            emptyTitle="Sem eventos"
            emptyDescription="Quando regras dispararem ou recuperarem, o histórico aparece aqui."
          />
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ActiveAlertRow({
  alert, primaryAction, muted = false,
}: {
  alert: ActiveAlert;
  primaryAction: React.ReactNode;
  muted?: boolean;
}) {
  // #166: cadência de renotify. Só mostra em status firing (ack não renotifica).
  const nextRenotifyLabel = formatNextRenotify(alert);

  return (
    <li className={`flex items-center justify-between gap-3 px-5 py-3 ${muted ? "opacity-80" : ""}`}>
      <div className="flex items-center gap-3">
        <Badge tone={SEVERITY_TONE[alert.severity]}>{alert.severity}</Badge>
        <div>
          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {alert.rule_kind}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {alert.message ?? "firing"} · {timeAgo(alert.since)}
            {alert.renotify_count > 0 && (
              <> · {alert.renotify_count} lembrete{alert.renotify_count > 1 ? "s" : ""}</>
            )}
            {nextRenotifyLabel && <> · {nextRenotifyLabel}</>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">{primaryAction}</div>
    </li>
  );
}

/**
 * "próx. aviso em Xmin/h" ou null se não aplicável.
 * Retorna null pra status != firing, sem next_renotify_at, ou já passou (raro
 * por causa do refetch de 10s — engine já teria disparado o novo aviso).
 */
function formatNextRenotify(alert: ActiveAlert): string | null {
  if (alert.status !== "firing" || !alert.next_renotify_at) return null;
  const now = Date.now();
  const next = new Date(alert.next_renotify_at).getTime();
  const diffMs = next - now;
  if (diffMs <= 0) return "próx. aviso a qualquer instante";
  const min = Math.round(diffMs / 60000);
  if (min < 60) return `próx. aviso em ${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `próx. aviso em ${h}h`;
  const d = Math.round(h / 24);
  return `próx. aviso em ${d}d`;
}
